import { OfferInstance } from '../models/Offer'
import * as qs from 'qs'
import { Utils } from './Utils'

export interface IAPIOfficeData {
  address: string
  city: string
  description: string
  id: number
  image: {
    url: string
  }
  location_latitude: number
  location_longitude: number
  name: string
  phone: string
  website: string
  zip_code: string
  distance?: number
  landingpage?: string
  show_flex_button: boolean
}

export interface IAPIRecommendationData {
  attachment_type: string
  count: number
  jaw_type: string
  recommendation_type: string
}

export interface IAPIOfferResponse {
  bone_grafting_required: boolean
  code?: string
  costs: number
  allowance: number
  insurance_bonus?: string
  insurance_type?: string
  material: string
  zip_code: number
  upper_jaw_recommendations?: IAPIRecommendationData[]
  lower_jaw_recommendations?: IAPIRecommendationData[]
}

export interface IAPISuccessResponse {
  success: boolean
}

export interface IAPIRequestReportData {
  email: string
  request_report: boolean
  request_more_info: boolean
  interests: {
    knochenaufbau: boolean
    angst_besiegen: boolean
    risikofaktoren: boolean
    qualifikationen: boolean
    allgemeine_informationen: boolean
    zahnersatz_auf_implantaten: boolean
  }
}

export type IAPIOfficesResponse = IAPIOfficeData[]

export interface IAPIService {
  fetchRecommendations: (offer: OfferInstance, apiKey?: string) => Promise<IAPIOfferResponse>
  calculateOffer: (offer: OfferInstance, apiKey?: string) => Promise<IAPIOfferResponse>
  fetchOffer: (offerId: string) => Promise<IAPIOfferResponse>
  fetchOffices: (offerId?: string, options?: any) => Promise<IAPIOfficesResponse>
  requestReport: (offerId: string, data: IAPIRequestReportData) => Promise<IAPISuccessResponse>
}

export class APIService implements IAPIService {
  private static BASE_PATH = '/api/v1/'

  private get _baseUrl(): string {
    const isLive = Utils.isLive()
    return isLive ? 'https://rechner.implacheck.de' : ''
  }

  async fetchRecommendations(offer: OfferInstance, apiKey?: string): Promise<IAPIOfferResponse> {
    const url = this._baseUrl + APIService.BASE_PATH + 'offers/recommend'

    const body = this._makeBodyForOffer(offer)
    const headers: any = { 'Content-Type': 'application/json' }
    if (apiKey) {
      headers['X-API-Key'] = apiKey
    }

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    })
    const json = await response.json()
    if (response.status !== 200) {
      throw new Error(json.message)
    }
    return json
  }

  async calculateOffer(offer: OfferInstance, apiKey?: string, multiplier?: number): Promise<IAPIOfferResponse> {
    const url = this._baseUrl + APIService.BASE_PATH + 'offers'

    const body = this._makeBodyForOffer(offer)
    const headers: any = { 'Content-Type': 'application/json' }
    if (apiKey) {
      headers['X-API-Key'] = apiKey
    }
    if (multiplier) {
      body.offer.multiplier = multiplier
    }

    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
    })

    const json = await response.json()
    if (response.status !== 200) {
      throw new Error(json.message)
    }
    return json
  }

  async fetchOffer(offerId: string): Promise<IAPIOfferResponse> {
    const url = this._baseUrl + APIService.BASE_PATH + 'offers/' + offerId

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  async fetchOffices(offerId?: string, options?: any): Promise<IAPIOfficesResponse> {
    let url
    if (offerId) {
      url =
        this._baseUrl +
        APIService.BASE_PATH +
        'offers/' +
        offerId +
        '/offices?' +
        qs.stringify(options || {}, { encode: false, arrayFormat: 'brackets' })
    } else {
      url =
        this._baseUrl +
        APIService.BASE_PATH +
        'offices?' +
        qs.stringify(options || {}, { encode: false, arrayFormat: 'brackets' })
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  async requestReport(offerId: string): Promise<IAPISuccessResponse> {
    const url = this._baseUrl + APIService.BASE_PATH + 'offers/' + offerId + '/request_report'

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return response.json()
  }

  async sendMail(officeId: string, data): Promise<IAPISuccessResponse> {
    const url = this._baseUrl + APIService.BASE_PATH + 'offices/' + officeId + '/send_mail'

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    return response.json()
  }

  private _makeBodyForOffer(offer: OfferInstance): any {
    const body: any = {
      offer: {},
    }
    if (offer.upperJawRequirement) {
      body.offer.upper_jaw_requirement_attributes = offer.upperJawRequirement.serializeForAPI()
    }
    if (offer.lowerJawRequirement) {
      body.offer.lower_jaw_requirement_attributes = offer.lowerJawRequirement.serializeForAPI()
    }
    body.offer.material = offer.material
    body.offer.insurance_type = offer.insuranceType
    body.offer.insurance_bonus = offer.insuranceBonus
    body.offer.zip_code = offer.zipCode
    body.offer.email = offer.email
    body.offer.bone_grafting_required = offer.boneGrafting
    body.offer.bone_grafting_type = offer.boneGraftingType
    return body
  }
}
